// Deterministic FBM height field shared by the main thread (initial load,
// CPU sampling) and the terrain worker (background window regeneration).
// The terrain is a pure function of world position; a height "window" is
// just this function cached over dims x dims centred on an origin.

// Base hill wavelength in world units. Longer waves + lower persistence
// = broad rolling swells rather than choppy noise.
export const HILL_SCALE = 650.0;

function Hash2i_(x, y) {
  let h = Math.imul(x, 0x27d4eb2d) ^ Math.imul(y, 0x165667b1);
  h = Math.imul(h ^ (h >>> 15), 0x85ebca6b);
  h = Math.imul(h ^ (h >>> 13), 0xc2b2ae35);
  h ^= h >>> 16;
  return (h >>> 0) / 4294967295;
}

function ValueNoise2_(x, y) {
  const xi = Math.floor(x);
  const yi = Math.floor(y);
  const xf = x - xi;
  const yf = y - yi;
  const ux = xf * xf * (3 - 2 * xf);
  const uy = yf * yf * (3 - 2 * yf);
  const a = Hash2i_(xi, yi);
  const b = Hash2i_(xi + 1, yi);
  const c = Hash2i_(xi, yi + 1);
  const d = Hash2i_(xi + 1, yi + 1);
  return a + (b - a) * ux + (c - a) * uy + (a - b - c + d) * ux * uy;
}

export function FBM2(x, y, octaves, persistence, lacunarity) {
  let amplitude = 1.0;
  let frequency = 1.0;
  let total = 0.0;
  let normalization = 0.0;
  for (let i = 0; i < octaves; ++i) {
    total += ValueNoise2_(x * frequency, y * frequency) * amplitude;
    normalization += amplitude;
    amplitude *= persistence;
    frequency *= lacunarity;
  }
  return total / normalization;
}

// Gradient (Perlin) noise with analytic derivatives (after iquilezles.org
// "noise derivatives"). Gradient noise is used for the eroded field rather
// than the value noise above: value noise carries visible lattice creases
// that derivative damping etches into scratch marks, and its hash makes
// the world origin a singular pit (every octave's lattice zero coincides
// there). Gradient noise is zero at every lattice point equally - no
// grain, no special points. Output roughly [-1, 1]; out = [value, dx, dy].
function GradientNoised2_(x, y, out) {
  const xi = Math.floor(x);
  const yi = Math.floor(y);
  const xf = x - xi;
  const yf = y - yi;
  const ux = xf * xf * (3 - 2 * xf);
  const uy = yf * yf * (3 - 2 * yf);
  const dux = 6 * xf * (1 - xf);
  const duy = 6 * yf * (1 - yf);

  const TAU = 6.283185307179586;
  let t = Hash2i_(xi, yi) * TAU;
  const gax = Math.cos(t), gay = Math.sin(t);
  t = Hash2i_(xi + 1, yi) * TAU;
  const gbx = Math.cos(t), gby = Math.sin(t);
  t = Hash2i_(xi, yi + 1) * TAU;
  const gcx = Math.cos(t), gcy = Math.sin(t);
  t = Hash2i_(xi + 1, yi + 1) * TAU;
  const gdx = Math.cos(t), gdy = Math.sin(t);

  const va = gax * xf + gay * yf;
  const vb = gbx * (xf - 1) + gby * yf;
  const vc = gcx * xf + gcy * (yf - 1);
  const vd = gdx * (xf - 1) + gdy * (yf - 1);
  const k3 = va - vb - vc + vd;

  // ~1.9 normalizes the practical range to [-1, 1].
  const NORM = 1.9;
  out[0] = NORM * (va + ux * (vb - va) + uy * (vc - va) + ux * uy * k3);
  out[1] = NORM * (
      gax + ux * (gbx - gax) + uy * (gcx - gax) + ux * uy * (gax - gbx - gcx + gdx) +
      dux * ((vb - va) + uy * k3));
  out[2] = NORM * (
      gay + ux * (gby - gay) + uy * (gcy - gay) + ux * uy * (gay - gby - gcy + gdy) +
      duy * ((vc - va) + ux * k3));
  return out;
}

// Derivative-damped FBM (I. Quilez, "Elevated" / iquilezles.org fbm
// article): a running sum of octave gradients damps each octave's
// contribution where the terrain is already steep - a one-line erosion
// model. Flats accumulate detail, flanks sweep smooth, ridgelines stay
// crisp, instead of plain FBM's uniform crinkle. The inter-octave
// rotation decorrelates the value-noise lattice so no axis-aligned grain
// survives.
const ROT_C = Math.cos(0.5);
const ROT_S = Math.sin(0.5);
const NOISED_TMP = [0, 0, 0];

export function FBM2Eroded(x, y, octaves, persistence, lacunarity, damping) {
  let sum = 0;
  let amp = 1;
  let norm = 0;
  let dx = 0;
  let dy = 0;
  let px = x;
  let py = y;
  for (let i = 0; i < octaves; ++i) {
    GradientNoised2_(px, py, NOISED_TMP);
    const n = NOISED_TMP[0];
    dx += NOISED_TMP[1];
    dy += NOISED_TMP[2];
    // The base octave IS the landform (the 650m swell) and passes
    // undamped; erosion only redistributes the detail octaves across it.
    const damp = i === 0 ? 1 : 1 / (1 + damping * (dx * dx + dy * dy));
    sum += amp * n * damp;
    norm += amp;
    amp *= persistence;
    const nx = (px * ROT_C - py * ROT_S) * lacunarity;
    const ny = (px * ROT_S + py * ROT_C) * lacunarity;
    px = nx;
    py = ny;
  }
  return sum / norm;
}

const _floatView = new Float32Array(1);
const _uintView = new Uint32Array(_floatView.buffer);

// Compact float32 -> float16 (truncating); heights are 0..1 so the
// flush-to-zero of sub-2^-14 values is irrelevant.
function ToHalfFloat_(val) {
  _floatView[0] = val;
  const f = _uintView[0];
  const sign = (f >>> 16) & 0x8000;
  const exp = ((f >>> 23) & 0xff) - 112;
  if (exp <= 0) return sign;
  if (exp >= 31) return sign | 0x7bff;
  return sign | (exp << 10) | ((f & 0x7fffff) >> 13);
}

// Eroded-field shaping parameters. 7 octaves reach ~10m wavelengths
// (the 1024 heightmap texel is 3.9m); damping 3.0 is the "swept flanks,
// crisp creases" end of the range (see docs/RENDERING.md). The range
// remap normalizes the field's observed [-0.77, 0.70] into [0, 1] with a
// little margin.
const ERODED_OCTAVES = 7;
const ERODED_PERSISTENCE = 0.5;
const ERODED_LACUNARITY = 2.0;
const ERODED_DAMPING = 3.0;
const ERODED_MIN = -0.80;
const ERODED_MAX = 0.75;

// Samples land on the absolute world lattice spaced dims/(res-1), so two
// windows whose origins are snapped to that lattice agree exactly where
// they overlap - a window swap never visibly moves the ground.
//
// `octaves` lets the far shell use a truncated field: damping accumulates
// octave-by-octave, so a 5-octave evaluation is bit-identical to the
// first five octaves of the full 7 - the shell's macro shapes agree
// exactly with the near window, differing only by the ~±5m detail
// octaves it omits (subpixel at shell distances).
export function GenerateHeightField(
    resolution, dims, originX, originZ, octaves = ERODED_OCTAVES) {
  const f32 = new Float32Array(resolution * resolution);
  const u16 = new Uint16Array(resolution * resolution);

  for (let row = 0; row < resolution; ++row) {
    const v = row / (resolution - 1);
    const worldZ = originZ + (0.5 - v) * dims;
    for (let col = 0; col < resolution; ++col) {
      const u = col / (resolution - 1);
      const worldX = originX + (u - 0.5) * dims;
      const e = FBM2Eroded(
          worldX / HILL_SCALE, worldZ / HILL_SCALE,
          octaves, ERODED_PERSISTENCE, ERODED_LACUNARITY,
          ERODED_DAMPING);
      const h = Math.min(Math.max(
          (e - ERODED_MIN) / (ERODED_MAX - ERODED_MIN), 0), 1);
      const i = row * resolution + col;
      f32[i] = h;
      u16[i] = ToHalfFloat_(h);
    }
  }

  return { f32, u16 };
}
